import { get_metric_info_from_identifier } from "../../Utils/chart_utils";

/* ==================================================
   ProcessedStatDataContainer Class
   ================================================== */

/**
 * Immutable class to act as a container for processed statistic data.
 * This class encapsulates an array of processed data objects and provides
 * methods to perform transformation of raw data and extract labels.

 */
export class ProcessedStatDataContainer {
  /**
   * Constructs a new instance.
   *
   * @param {Array} rawData - The raw unprocessed data.
   * @param {Array} metrics - An array of metric labels.
   * @param {Array} playerData - List of player objects for lookups.
   * @param {Array} groups - List of group objects for lookups.
   * @param {string} metricType - The metric type (default "nutrition").
   */
  constructor(rawData, metrics, playerData, groups, metricType = "nutrition") {
    this._dataList = this._transformData(rawData, metrics, playerData, groups, metricType);
    Object.freeze(this._dataList);
    Object.freeze(this);
  }

  /**
   * Transforms raw data into a structured format.
   *
   * The method processes each metric label by:
   *   1. Initializing option and data arrays for every metric.
   *   2. Looping over each raw data entry, deriving a user-friendly group name.
   *   3. Building an option object for the entry (or using a default if needed).
   *   4. Extracting and merging records that include the target metric.
   *   5. Sorting the consolidated records by date.
   *
   * @param {Array} rawData - The raw data array.
   * @param {Array} metrics - Array of metric labels to process.
   * @param {Array} playerData - Array of player objects.
   * @param {Array} groups - Array of group objects.
   * @param {string} metricType - The metric type.
   * @returns {Array} Array of processed data objects, each with { name, optionsV2, data }.
   */
  _transformData(rawData, metrics, playerData, groups, metricType) {
    /**
     * Derives a human-friendly display name from a raw group key.
     *
     * This function interprets the raw key (e.g., "player-5", "group-2", "pos-midfield", or "full-team")
     * to produce a user-friendly name:
     *   - For keys beginning with "player-", it looks up the player's first and last name.
     *   - For keys beginning with "group-", it looks up the group's name.
     *   - For keys beginning with "pos-", it returns the substring after the hyphen.
     *   - For other cases (like "full-team"), it returns a predefined name or the key itself.
     *
     * @param {string} groupKeyPre - The raw key associated with a group (e.g., "player-5").
     * @param {number} idx - Index of the entry used as a fallback for naming if lookup fails.
     * @returns {string} A human-readable display name for the group.
     */
    const getGroupDisplayName = (groupKeyPre, idx) => {
      // Case 1: The key represents an individual player.
      if (groupKeyPre.startsWith("player-")) {
        const playerId = parseInt(groupKeyPre.split("-")[1], 10);
        const player = playerData.find((p) => p.player_id === playerId);
        return player ? `${player.first_name} ${player.last_name}` : `Unknown Player ${idx + 1}`;
      }
      // Case 2: The key represents a player group.
      else if (groupKeyPre.startsWith("group-")) {
        const groupId = parseInt(groupKeyPre.split("-")[1], 10);
        const group = groups.find((g) => g.player_group_id === groupId);
        return group ? group.name : `Unknown Group ${idx + 1}`;
      }
      // Case 3: The key represents a position.
      else if (groupKeyPre.startsWith("pos-")) {
        // Simply return the substring after "pos-".
        return groupKeyPre.split("-")[1];
      }
      // Case 4: For other keys (e.g., "full-team"), use a predefined name or return the key directly.
      else {
        return groupKeyPre === "full-team" ? "Full Team" : groupKeyPre;
      }
    };

    // Helper function: Build the option object for the data entry.
    const buildOption = (currentDataObj, groupKey, metric) => {
      // Check if an original option exists for the current metric.
      const originalOption = currentDataObj.optionsV2?.find(
        (opt) => opt.metric_type === metricType && opt.name === metric
      );
      // If found, update its name with the display name, else return a default option.
      return originalOption
        ? { ...originalOption, name: groupKey }
        : {
          testing_metric_id: 1,
          name: groupKey,
          low_good: false,
          num_decimals: 2,
          is_percentage: false,
          metric_type: metricType,
          metadata: { tags: ["do_not_split"] },
        };
    };

    // Helper function: Extract records from the data array if they contain the metric value.
    const extractRecords = (groupData, innerMetricKey, groupKey) => {
      return groupData
        .map((rec) =>
          rec.hasOwnProperty(innerMetricKey) ? { date: rec.date, [groupKey]: rec[innerMetricKey] } : null
        )
        .filter((record) => record !== null);
    };

    // Helper function: Merge a record into the data array based on the date.
    const mergeRecord = (dataArr, record) => {
      // Look for an existing record with the same date.
      const existingIndex = dataArr.findIndex((obj) => obj.date === record.date);
      if (existingIndex !== -1) {
        // If found, merge the new data into the existing record.
        dataArr[existingIndex] = { ...dataArr[existingIndex], ...record };
      } else {
        // Otherwise, add the new record.
        dataArr.push(record);
      }
    };

    // Calculate average bodyweight of all players
    const calculateAverageBodyWeight = () => {
      if (!playerData || playerData.length === 0) return null;
      
      const playersWithBodyWeight = playerData.filter(player => 
        player.bodyweight_baseline !== undefined && 
        player.bodyweight_baseline !== null && 
        !isNaN(player.bodyweight_baseline)
      );
      
      if (playersWithBodyWeight.length === 0) return null;
      
      const totalBodyWeight = playersWithBodyWeight.reduce((sum, player) => 
        sum + parseFloat(player.bodyweight_baseline), 0
      );
      
      return totalBodyWeight / playersWithBodyWeight.length;
    };

    // Calculate the average bodyweight once
    const averageBodyWeight = calculateAverageBodyWeight();

    // Initialize the container for the processed output.
    const output = [];

    // Process every metric label.
    metrics.forEach((metric) => {
      const optionsV2 = []; // To hold options for each processed entry.
      const dataArr = []; // To merge and accumulate data records.
      const innerMetricKey = metric;

      // Loop through each raw data entry.
      rawData.forEach((entry, idx) => {
        // Each entry is keyed by a selection id, e.g. "player-1" or "full-team". Possibly a player, group, or position.
        const groupKeyPre = Object.keys(entry)[0];
        // Derive a human-friendly name from the key.
        const groupKey = getGroupDisplayName(groupKeyPre, idx);
        // Get the associated data object for the key.
        const currentDataObj = entry[groupKeyPre];
        // Build or retrieve an option object and add it to the options array.
        const option = buildOption(currentDataObj, groupKey, metric);
        
        // If this is a Body Weight metric and we have an average bodyweight, set it as the baseline
        if (averageBodyWeight !== null) {
          option.baseline = averageBodyWeight;
        }
        
        optionsV2.push(option);
        
        // Process the data records: extract those that contain the target metric.
        const groupData = currentDataObj.data;
        const records = extractRecords(groupData, innerMetricKey, groupKey);
        // Merge each record into a consolidated data array.
        records.forEach((record) => mergeRecord(dataArr, record));
      });

      // Sort the merged data records by date (assumes "YYYY-MM-DD" format).
      dataArr.sort((a, b) => a.date.localeCompare(b.date));
      // Push the processed result for the current metric into the output array.
      output.push({ name: metric, optionsV2, data: dataArr });
    });
    return output;
  }
  /**
   * Static method to convert an array of stat metrics to metric labels.
   *
   * @param {Array} statMetrics - Array of stat metrics.
   * @param {object} all_metric_types - Mapping of metric types.
   * @param {string} metricType - The metric type (default "nutrition").
   * @returns {Array} Array of metric labels.
   */
  static getMetricLabels(statMetrics, all_metric_types, metricType = "nutrition") {
    const metricIds = statMetrics
      .map((metric) => get_metric_info_from_identifier(metric)?.metric_id)
      .filter((metricId) => metricId !== undefined && metricId !== null);
    const metricLabels = metricIds
      .map((metricId) => {
        const metric_info = Object.values(all_metric_types?.[metricType] || {})
          .flat()
          .find((metric) => metric.unique_identifier === metricId);
        return metric_info?.name;
      })
      .filter((label) => label);
    return metricLabels;
  }
  /**
  * Returns an array indicating for each processed metric whether 
  * at least one record contains a valid numerical value (excluding the "date" field).
  *
  * @returns {Array} Boolean array, one for each processed data object.
  */
  getNonEmptyFlags() {
    return this._dataList.map((processed) =>
      processed.data.some((record) =>
        Object.entries(record).some(
          ([key, value]) => key !== "date" && value !== null && !isNaN(value)
        )
      )
    );
  }
  /**
   * Getter for the processed data list.
   *
   * @returns {Array} Array of processed data objects.
   */
  get dataList() {
    return this._dataList;
  }

  /**
   * Extracts metric labels from the processed data.
   *
   * @returns {Array} Array of metric names.
   */
  getLabels() {
    return this._dataList.map((item) => item.name);
  }
}